import React from "react";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import { orderBy } from "lodash-es";
/** =========================
 *  Helpers
 *  ========================= */
import { smartTwoLine, cx, applyTextStyle, formatIndicatorValue, computeSmartSpans } from "../../helpers/CardsGrid";
/** =========================
 *  Types
 *  ========================= */
import { TextStyle, DashboardConfig, Indicator, Section, CardData } from "../../types/CardsGrid";
import { useLocation } from "@/Common/hooks/Location/useLocation";

/** =========================
 *  Config
 *  ========================= */

const config: DashboardConfig = {
	behavior: { labelInlineDefault: true, showLabelDefault: true },
	numbers: {
		negativeStyle: "parentheses", // or "minus"
	},
	indicators: {
		//? Longevity
		hiredDate: { format: "date", pattern: "MMM D, YYYY", align: "left" },
		longevityYears: { format: "number", decimals: 0, align: "right" },
		compensation: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		terminationDate: { format: "date", pattern: "MMM D, YYYY", align: "left" },
		//? Performance Metrics
		backEndPvr: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		unitAdj: { format: "string", align: "right" },
		unfundedDeals: { format: "number", decimals: 0, align: "right" },
		//? Sales Information
		frontEndPVR: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		reconBackouts: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		//? Reviews
		franchise: { format: "string", align: "left" },
		requiredReviews: { format: "number", decimals: 2, align: "right" },
		actualPenetration: { format: "number", decimals: 2, suffix: "%", align: "right" },
		//? Deductions
		buyout: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		hourlyDraw: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		pto: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		csiBonus: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		incentives: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		advancedCommissions: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		titleFees: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		payoffShortageReimbursement: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		roChargebackReimbursement: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		flatDraw: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		longevity: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		spiff: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		commissionDraw: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		birdDogReimbursement: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		missingDownPayment: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		missingDpt: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		commissions: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		titleFeesReimb: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		incentivesReimb: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		temporaryGuarantee: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		payoffShortage: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		birdDog: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		training: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		missingDptReimb: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		holiday: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		juryDuty: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		retentionPay: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		default: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		shortages: { format: "currency", currency: "USD", decimals: 2, align: "right" },
		gps: { format: "currency", currency: "USD", decimals: 2, align: "right" },
	},
	text: {
		cardTitle: { bold: false, transform: "none", breakLine: false },
		sectionTitle: { bold: false, transform: "none", breakLine: true },
		indicatorLabel: { bold: true, transform: "none", breakLine: true },
		indicatorValue: { bold: true, transform: "none", breakLine: false, ellipsis: true, tooltip: true },
	},
};

/** =========================
 *  Grid
 *  ========================= */

export function CardsDenseGrid(props: { loading: boolean; data: CardData[] }) {
	const data: CardData[] = props.data;
	const containerMax = 1350;

	const gridRef = React.useRef<HTMLDivElement | null>(null);

	const sorted = orderBy(data, [(item) => item.sections?.length ?? 0], ["desc"]);
	const dataMapped = computeSmartSpans(sorted);

	return (
		<div ref={gridRef} className="mx-auto mt-5 w-full py-4 px-1" style={{ maxWidth: containerMax }}>
			<div className="grid grid-cols-12 gap-3 items-stretch" style={{ gridAutoFlow: "dense" }}>
				{props.loading ? <LoadingCardsRow /> : dataMapped.map((card) => <MetricCard key={card.id} card={card} config={config} />)}
			</div>
		</div>
	);
}

/** =========================
 *  Card
 *  ========================= */

function MetricCard({ card, config }: { card: CardData & { span: number; shouldScroll: boolean }; config: DashboardConfig }) {
	const cardTitle = config.text.cardTitle.breakLine ? smartTwoLine(card.title) : card.title;

	const shouldScroll = card.shouldScroll; // ✅ rule you want
	const minWidth = shouldScroll ? 150 : undefined;

	return (
		<div
			className="h-full rounded-xl border border-slate-200 bg-white shadow-[0_1px_6px_0_#00000033]"
			style={{ gridColumn: `span ${card.span}` }} // ✅ THIS is the key
		>
			<div className="p-5 pb-3 h-full flex flex-col">
				<div className={cx("mb-3 text-2xl !text-primary !font-montserrat", applyTextStyle(config.text.cardTitle))}>{cardTitle}</div>

				<div className="min-w-0 flex-1">
					<div className="overflow-x-auto overflow-y-hidden pb-2">
						<div className="flex w-full">
							{card.sections.length > 0 ? (
								card.sections.map((section, idx) => (
									<div
										key={section.id}
										className={cx("py-1 px-3", idx > 0 && "border-l border-disabled")}
										style={{
											minWidth,
											flex: "1 1 0%",
										}}
									>
										<SectionBlock section={section} config={config} />
									</div>
								))
							) : (
								<div className="flex items-center justify-center w-full h-full py-8">
									<p className="text-sm text-default italic">No records found</p>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

/** =========================
 *  Section
 *  ========================= */

function SectionBlock({ section, config }: { section: Section; config: DashboardConfig }) {
	const title = (section.title ?? "").trim();
	const showTitle = title.length > 0;

	// Render title as max 2 lines, but ALWAYS reserve 2-line height to align values
	const renderedTitle = config.text.sectionTitle.breakLine ? smartTwoLine(title) : title;

	return (
		<div className="flex flex-col min-w-0">
			{showTitle ? (
				<div
					className={cx(
						"text-[15px] text-slate-600 min-w-0 leading-tight",
						// ✅ reserve same height for all titles (2 lines)
						// Adjust 2.25rem if your font-size/line-height differs.
						"min-h-[2.25rem]",
						// ✅ clamp to 2 lines (no more than 2)
						"line-clamp-2",
						applyTextStyle(config.text.sectionTitle)
					)}
				>
					<Tooltip title={title} placement="top" arrow>
						<span>{renderedTitle}</span>
					</Tooltip>
				</div>
			) : (
				// ✅ if no title, still reserve the same space so values align
				<div className="min-h-[2.25rem]" />
			)}

			{/* values area */}
			<div className="mt-2 flex flex-col gap-2 min-w-0">
				{section.indicators.map((ind) => (
					<IndicatorRow key={`${section.id}-${ind.key}`} indicator={ind} config={config} />
				))}
			</div>
		</div>
	);
}

/** =========================
 *  Indicator
 *  ========================= */

function IndicatorRow({ indicator, config }: { indicator: Indicator; config: DashboardConfig }) {
	const location = useLocation();
	const { timezone } = location ?? {};
	const fmt = config.indicators[indicator.key];
	const labelInline = config.behavior?.labelInlineDefault ?? true;
	const showLabelDefault = config.behavior?.showLabelDefault ?? true;

	const rawLabel = (indicator.title ?? "").trim();
	const hasLabel = showLabelDefault && rawLabel.length > 0;

	const { text: valueText, isNegative } = formatIndicatorValue(indicator.value, fmt, config, timezone);
	const valueAlign = fmt?.align ?? config.text.indicatorValue.align ?? "right";
	const labelAlign = config.text.indicatorLabel.align ?? "left";

	const valueStyle: TextStyle = {
		...config.text.indicatorValue,
		align: valueAlign,
		bold: (fmt as any)?.bold ?? config.text.indicatorValue.bold,
		italic: (fmt as any)?.italic ?? config.text.indicatorValue.italic,
		underline: (fmt as any)?.underline ?? config.text.indicatorValue.underline,
	};

	const wantsTooltip = Boolean(valueStyle.tooltip);
	const shouldTruncate = Boolean(valueStyle.ellipsis);

	const valueNode = (
		<div
			className={cx("text-sm min-w-0", applyTextStyle(valueStyle), isNegative ? "text-alert" : "text-default")}
			title={!wantsTooltip ? valueText : undefined}
		>
			{valueText}
		</div>
	);

	// Wrapper classes depend on whether label exists
	const rowClass = labelInline
		? hasLabel
			? "flex items-baseline justify-between gap-3"
			: "flex items-baseline justify-end" // ✅ no label => value can align right properly
		: "flex flex-col gap-1";

	const valueWrapClass =
		labelInline && hasLabel
			? cx("min-w-0", valueAlign === "right" && "text-right")
			: cx("min-w-0 w-full", valueAlign === "right" && "text-right"); // ✅ full width when solo value

	return (
		<div className={cx("min-w-0", rowClass)}>
			{hasLabel ? (
				<div className={cx("text-xs text-slate-500 min-w-0", applyTextStyle({ ...config.text.indicatorLabel, align: labelAlign }))}>
					{config.text.indicatorLabel.breakLine ? smartTwoLine(rawLabel) : rawLabel}
				</div>
			) : null}

			{wantsTooltip && shouldTruncate ? (
				<div className={valueWrapClass}>
					<Tooltip title={valueText} placement="right" arrow>
						<span>{valueNode}</span>
					</Tooltip>
				</div>
			) : (
				<div className={valueWrapClass}>{valueNode}</div>
			)}
		</div>
	);
}

/** =========================
 *  Loading (Skeleton)
 *  ========================= */

function LoadingCardsRow() {
	// 2 small + 1 wide, all in ONE row
	const items = [
		{ id: "sk-a", span: 3 },
		{ id: "sk-b", span: 3 },
		{ id: "sk-c", span: 6 },
	];

	return (
		<>
			{items.map((it) => (
				<LoadingMetricCard key={it.id} span={it.span} />
			))}
		</>
	);
}

function LoadingMetricCard({ span }: { span: number }) {
	return (
		<div
			className="h-[160px] rounded-xl border border-slate-200 bg-white shadow-[0_1px_6px_0_#00000033]"
			style={{ gridColumn: `span ${span}` }}
		>
			<div className="p-7 pb-3 h-full flex flex-col">
				{/* Title skeleton */}
				<Skeleton variant="rounded" height={26} className="w-2/3" />

				{/* Body skeleton */}
				<div className="mt-7 flex-1">
					<div className="flex gap-6">
						{/* Left column */}
						<div className="flex-1 space-y-4">
							<Skeleton variant="rounded" height={14} className="w-3/4" />
							<Skeleton variant="rounded" height={14} className="w-2/3" />
						</div>

						{/* Right column */}
						<div className="flex-1 space-y-4">
							<Skeleton variant="rounded" height={14} className="w-3/4" />
							<Skeleton variant="rounded" height={14} className="w-2/3" />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
